import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  MapPin, 
  PlusCircle,
  AlertCircle,
  PawPrint,
} from 'lucide-react';
import { Button } from '../ui/button';
import { FEED_TYPES } from '../../hooks/notifications/useCombinedFeed';

/**
 * Empty state component for the feed
 */
const FeedEmptyState = ({ 
  filter = FEED_TYPES.ALL, 
  hasCoordinates = true,
  onCreatePost,
  hasError = false,
  onRetry,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  // Error state
  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('feed_error_title', 'Something went wrong')}
        </h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-xs">
          {t('feed_error_description', "We couldn't load the feed. Please try again.")}
        </p>
        {onRetry && (
          <Button 
            onClick={onRetry} 
            variant="outline"
            className="min-w-[120px]"
          >
            {t('try_again', 'Try Again')}
          </Button>
        )}
      </div>
    );
  }

  // No location state
  if (!hasCoordinates) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
          <MapPin className="w-8 h-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('location_needed_title', 'Location Needed')}
        </h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-xs">
          {t('location_needed_description', 'Enable location services to see posts in your area.')}
        </p>
        <Button 
          onClick={() => navigate('/profile')}
          variant="outline"
          className="min-w-[120px]"
        >
          {t('set_location', 'Set Location')}
        </Button>
      </div>
    );
  }

  // Get filter-specific messaging
  const getFilterMessage = () => {
    switch (filter) {
      case FEED_TYPES.LOST:
        return {
          title: t('no_lost_animals', 'No Lost Animal Posts'),
          description: t('no_lost_animals_desc', "Good news! No lost pets reported in your area."),
          ctaLabel: t('report_lost_animal', 'Report Lost Pet'),
          ctaType: 'lost-animal',
        };
      case FEED_TYPES.FOUND:
        return {
          title: t('no_found_animals', 'No Found Animal Posts'),
          description: t('no_found_animals_desc', 'No found animal reports in your area yet.'),
          ctaLabel: t('report_found_animal', 'Report Found Pet'),
          ctaType: 'found-animal',
        };
      case FEED_TYPES.PSA:
        return {
          title: t('no_psas', 'No Announcements'),
          description: t('no_psas_desc', 'No public service announcements in your area.'),
          ctaLabel: t('create_psa', 'Create Announcement'),
          ctaType: 'psa',
        };
      default:
        return {
          title: t('no_posts', 'No Posts Yet'),
          description: t('no_posts_desc', 'Be the first to share something with your community!'),
          ctaLabel: t('create_post', 'Create Post'),
          ctaType: null,
        };
    }
  };

  const { title, description, ctaLabel, ctaType } = getFilterMessage();

  const handleCreatePost = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (onCreatePost) {
      onCreatePost(ctaType);
    } else {
      navigate('/notifications/create-post', { state: { type: ctaType } });
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
        {filter === FEED_TYPES.ALL ? (
          <PawPrint className="w-8 h-8 text-muted-foreground" />
        ) : (
          <Search className="w-8 h-8 text-muted-foreground" />
        )}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground mb-6 max-w-xs">
        {description}
      </p>
      <Button 
        onClick={handleCreatePost}
        className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
      >
        <PlusCircle className="w-4 h-4" />
        {ctaLabel}
      </Button>
    </div>
  );
};

export default FeedEmptyState;
